import { UserProfileRole } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { Tile } from '@components/shared/tile.component';
import { useGetCategories } from '@queries/category.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { formatProper } from '@utils/text.util.ts';
import { EditIcon } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { TechnologyTechnologyModal } from './technology-technology-modal.component';

export const TechnologyDetailsTile: React.FC = () => {
  const { technologyId } = useParams();
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const { data: categories = [] } = useGetCategories();
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  const subcategories = categories.flatMap((category) => category.subcategories);
  const subcategory = subcategories.find((subcategory) => subcategory.id === technology?.subcategoryId);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Tile
        header={{
          iconButton: canEdit
            ? {
                icon: <EditIcon size={16} />,
                onClick: handleOpenModal,
              }
            : undefined,
          title: 'Details',
        }}
        hoverReveal={canEdit}
        isLoading={isTechnologyLoading}
      >
        <div className="flex flex-col gap-4">
          {technology && (
            <div className="flex flex-col">
              <div className="text-lg font-medium text-gray-800">{technology.name}</div>
              <BlueLabel text={`${formatProper(technology.type)} Technology`} />
            </div>
          )}

          <div className="flex flex-col gap-3 text-sm text-gray-600 font-medium">
            {technology?.category && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  {technology.category.description ? (
                    <TooltipComponent
                      side="right"
                      className="text-xs whitespace-pre-wrap font-normal"
                      trigger={
                        <span className="font-medium cursor-help border-b border-dashed border-gray-300">
                          {technology.category.name}
                        </span>
                      }
                    >
                      {technology.category.description}
                    </TooltipComponent>
                  ) : (
                    <span className="font-medium">{technology.category.name}</span>
                  )}
                </div>
                <BlueLabel text="Category" />
              </div>
            )}

            {subcategory && (
              <div className="flex flex-col">
                <div>
                  {subcategory.description ? (
                    <TooltipComponent
                      side="right"
                      className="text-xs whitespace-pre-wrap font-normal"
                      trigger={
                        <span className="font-medium cursor-help border-b border-dashed border-gray-300">
                          {subcategory.name}
                        </span>
                      }
                    >
                      {subcategory.description}
                    </TooltipComponent>
                  ) : (
                    <span className="font-medium">{subcategory.name}</span>
                  )}
                </div>
                <BlueLabel text="Subcategory" />
              </div>
            )}
          </div>

          {technology?.description && (
            <ExpandableText
              className="text-xs font-normal text-gray-500"
              text={technology.description}
              maxLines={4}
            />
          )}
        </div>
      </Tile>
      {technology && canEdit && (
        <TechnologyTechnologyModal
          isEdit={true}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          technology={technology}
        />
      )}
    </>
  );
};
